import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashApi, type AcceptanceItem } from "@/lib/dashApi";
import { useAuth } from "@/lib/auth";
import { fmtRelative } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, StatusPill } from "@/components/dash/ui";

// Mirrors backend acceptance.VALID_TRANSITIONS.
const TRANSITIONS: Record<string, string[]> = {
  open: ["under_review", "needs_tuning", "confirmed", "incorrect", "not_useful", "deferred", "resolved"],
  under_review: ["needs_tuning", "confirmed", "incorrect", "not_useful", "deferred", "resolved", "open"],
  needs_tuning: ["under_review", "confirmed", "resolved", "deferred", "open"],
  confirmed: ["open", "resolved"],
  incorrect: ["open"],
  not_useful: ["open"],
  deferred: ["open", "under_review"],
  resolved: ["open"],
};
const FILTERS = ["active", "open", "under_review", "needs_tuning", "confirmed", "incorrect", "not_useful", "deferred", "resolved", "all"];

const SEV_TONE: Record<string, string> = {
  critical: "text-rose-300", warning: "text-amber-300", info: "text-sky-300",
};

export default function AcceptanceCentrePage() {
  const auth = useAuth();
  const isAdmin = (auth.user?.roles ?? []).includes("admin");
  const [filter, setFilter] = useState("active");
  const qc = useQueryClient();
  const ov = useQuery({ queryKey: ["acceptance-overview"], queryFn: dashApi.acceptanceOverview });
  const q = useQuery({ queryKey: ["acceptance-items", filter], queryFn: () => dashApi.acceptanceItems(filter === "all" ? undefined : filter) });

  const seed = useMutation({
    mutationFn: () => dashApi.seedValidationItems(),
    onSuccess: () => qc.invalidateQueries(),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Acceptance Centre"
        description="IRONDash-only acceptance & validation ledger. Findings reference safe IDs/aggregates only and never write to a source system. KPI mismatches can be seeded from live validation; a reviewer decides the outcome."
        right={
          isAdmin && (
            <button
              onClick={() => seed.mutate()}
              disabled={seed.isPending}
              className="rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-1.5 text-xs font-medium text-sky-200 hover:bg-sky-500/20 disabled:opacity-50"
            >
              {seed.isPending ? "Seeding…" : "Seed from KPI validation"}
            </button>
          )
        }
      />

      {ov.data && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
          <SummaryTile label="Total" value={ov.data.total} />
          <SummaryTile label="Active" value={ov.data.active} tone={ov.data.active ? "amber" : undefined} />
          <SummaryTile label="Confirmed" value={ov.data.by_status.confirmed ?? 0} />
          <SummaryTile label="Needs tuning" value={ov.data.by_status.needs_tuning ?? 0} />
          <SummaryTile label="Incorrect" value={ov.data.by_status.incorrect ?? 0} tone={ov.data.by_status.incorrect ? "rose" : undefined} />
          <SummaryTile label="Not useful" value={ov.data.by_status.not_useful ?? 0} />
        </div>
      )}
      {seed.data && (
        <p className="rounded bg-sky-500/10 px-3 py-2 text-xs text-sky-200">
          Seeded {seed.data.seeded.created} new item(s) from validation ({seed.data.seeded.skipped} already open). KPI results: {JSON.stringify(seed.data.counts)}.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-full px-3 py-1 text-xs ${filter === f ? "bg-sky-500/20 text-sky-200 ring-1 ring-sky-500/40" : "bg-slate-800 text-slate-400 hover:text-slate-200"}`}
          >
            {f}
          </button>
        ))}
      </div>

      <Panel title="Acceptance items">
        {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : !q.data?.items.length ? (
          <EmptyState>No acceptance items for this filter.</EmptyState>
        ) : (
          <div className="space-y-3">
            {q.data.items.map((it) => (
              <ItemRow key={it.id} item={it} isAdmin={isAdmin} onChanged={() => qc.invalidateQueries()} />
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function SummaryTile({ label, value, tone }: { label: string; value: number; tone?: "amber" | "rose" }) {
  const c = tone === "amber" ? "text-amber-300" : tone === "rose" ? "text-rose-300" : "text-slate-100";
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-lg font-semibold ${c}`}>{value}</div>
    </div>
  );
}

function ItemRow({ item, isAdmin, onChanged }: { item: AcceptanceItem; isAdmin: boolean; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const mut = useMutation({
    mutationFn: (body: Parameters<typeof dashApi.updateAcceptanceItem>[1]) => dashApi.updateAcceptanceItem(item.id, body),
    onSuccess: onChanged,
  });
  const events = useQuery({ queryKey: ["acceptance-events", item.id], queryFn: () => dashApi.acceptanceItemEvents(item.id), enabled: open });
  const next = TRANSITIONS[item.status] ?? [];
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/30 px-3 py-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold ${SEV_TONE[item.severity] ?? "text-slate-300"}`}>{item.severity}</span>
            <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-400">{item.item_type}</span>
            {item.source_code && <span className="text-[10px] text-slate-500">{item.source_code}</span>}
            {item.origin === "auto" && <span className="text-[10px] text-sky-400">auto</span>}
          </div>
          <div className="mt-0.5 truncate text-sm text-slate-200">{item.title}</div>
          {item.source_comparison && <div className="mt-0.5 text-xs text-slate-500">{item.source_comparison}</div>}
          {item.evidence_note && <div className="mt-0.5 text-xs text-slate-400">Evidence: {item.evidence_note}</div>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <StatusPill status={item.status} />
          {isAdmin && next.length > 0 && (
            <select
              value=""
              onChange={(e) => { if (e.target.value) mut.mutate({ status: e.target.value }); }}
              className="rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-[11px] text-slate-200 [color-scheme:dark]"
            >
              <option value="">Move to…</option>
              {next.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          <button onClick={() => setOpen((o) => !o)} className="text-[11px] text-slate-500 hover:text-slate-300">
            {open ? "hide" : "history"}
          </button>
        </div>
      </div>
      {item.suggested_improvement && <div className="mt-1 text-xs text-emerald-300/80">Suggested: {item.suggested_improvement}</div>}
      {open && (
        <div className="mt-2 border-t border-slate-800 pt-2 text-xs text-slate-500">
          {events.data?.events.length ? (
            <ul className="space-y-1">
              {events.data.events.map((e) => (
                <li key={e.id}>
                  <span className="text-slate-400">{fmtRelative(e.ts)}</span> · {e.event_type}
                  {e.from_status && ` (${e.from_status} → ${e.to_status})`}
                  {e.note && <span className="text-slate-500"> — {e.note}</span>}
                  {e.actor_email && <span className="text-slate-600"> · {e.actor_email}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <span>No events.</span>
          )}
        </div>
      )}
    </div>
  );
}
